import get from "lodash/get";
import { getUsers } from "../../user/services/user.service";
import { getCars } from "../../car/services/car.service";
import { getWarranties } from "../../warranty/services/warranty.service";
import { getCompanies } from "../../company/services/company.service";

export const BREAKDOWN_STATUSES = {
  0: "Nierozpoczęta",
  1: "Rozpoczęta",
  2: "W toku",
  3: "Zamknięta",
};

export const DATE_FORMAT = "DD-MM-YYYY";
export const DATE_TIME_FORMAT = "DD-MM-YYYY HH:mm";
export const DATE_MONTH_FORMAT = "MM/yyyy";
export const MOMENT_MONTH_FORMAT = "MM/YYYY";

export const TRANSLATIONS = {
  lp: "LP",
  id: "ID",
  createdat: "Data utworzenia",
  datefrom: "Data od",
  dateto: "Data do",
  price: "Cena całkowita",
  companyname: "Nazwa firmy",
  vatid: "NIP",
  manufacturername: "Marka",
  modelname: "Model",
  carvin: "VIN",
  warrantytype: "Nazwa pakietu",
  warrantyduration: "Czas trwania pakietu",
  warrantyprice: "Cena pakietu",
  options: "Opcje dodatkowe",
  group: "Grupa awarii",
  numberofbreakdowns: "Liczba awarii",
  numberofwarrantys: "Liczba gwarancji",
  sumbreakdownsprice: "Suma kosztów awarii",
  warrantypricesum: "Suma cen gwarancji",
  voivodeship: "Województwo",
  incidentDate: "Data wystąpienia awarii",
  notes: "Notatki",
};

const USERS_TABLE_CONFIG = {
  columns: [
    {
      caption: "Nazwa użytkownika",
      dataField: "name",
      sort: true,
    },
    {
      caption: "Email",
      dataField: "email",
    },
    {
      caption: "Firma",
      dataField: "company.name",
    },
    {
      caption: "Rola",
      dataField: "role",
      renderCell: (role) => {
        switch (role) {
          case 0:
            return "Administrator";
          case 1:
            return "Techniczny";
          case 2:
            return "Klient";
          default:
            return "Brak";
        }
      },
    },
    {
      caption: "Aktywny",
      dataField: "active",
      renderCell: (active) => (active ? "Tak" : "Nie"),
    },
  ],
  noItemsFoundText: "No users found",
  initialSort: {
    sortField: "name",
    sortDirection: "asc",
  },
  filterFields: "name,email",
};

const WARRANTIES_TABLE_CONFIG = {
  columns: [
    {
      caption: "Data od",
      dataField: "dateFrom",
      sort: true,
    },
    {
      caption: "Data do",
      dataField: "dateTo",
      sort: true,
    },
    {
      caption: "Marka",
      dataField: "car.manufacturer.name",
    },
    {
      caption: "VIN",
      dataField: "car.vin",
      sort: true,
    },
    {
      caption: "Pakiet",
      dataField: "dictWarrantyType.name",
    },
    {
      caption: "Długość Pakietu",
      dataField: "dictWarrantyType.duration",
    },
    {
      caption: "Nazwa firmy",
      dataField: "car.company.name",
    },
    {
      caption: "NIP",
      dataField: "car.company.vatId",
    },
  ],
  noItemsFoundText: "Brak gwarancji spelniających kryteria",
  initialSort: {
    sortField: "createdAt",
    sortDirection: "desc",
  },
  filterFields: "car.vin",
};

const CARS_TABLE_CONFIG = {
  columns: [
    {
      caption: "VIN",
      dataField: "vin",
      renderCell: (vin) => vin.toUpperCase(),
      sort: true,
    },
    {
      caption: "Marka",
      dataField: "manufacturer.name",
    },
    {
      caption: "Model",
      dataField: "model.name",
    },
    {
      caption: "Rok",
      dataField: "year",
    },
    {
      caption: "Przebieg",
      dataField: "mileage",
    },
    {
      caption: "Firma",
      dataField: "company.name",
    },
  ],
  noItemsFoundText: "Brak samochodów",
  initialSort: {
    sortField: "createdAt",
    sortDirection: "desc",
  },
  filterFields: "vin,company.name,manufacturer.name",
};

const COMPANIES_TABLE_CONFIG = {
  columns: [
    {
      caption: "Nazwa firmy",
      dataField: "name",
      sort: true,
    },
    {
      caption: "NIP",
      dataField: "vatId",
      sort: true,
    },
    {
      caption: "Adres",
      dataField: "main_address.address",
      renderCell: (_, item) => {
        const route = get(item, "main_address.route", "");
        const street_number = get(item, "main_address.street_number", "");
        const postal_code = get(item, "main_address.postal_code", "");
        const locality = get(item, "main_address.locality", "");

        return `${route} ${street_number}, ${postal_code} ${locality}`;
      },
    },
    {
      caption: "Aktywny",
      dataField: "active",
      renderCell: (value) => (value ? "Tak" : "Nie"),
    },
  ],
  noItemsFoundText: "Brak firm spelniających kryteria",
  initialSort: {
    sortField: "createdAt",
    sortDirection: "desc",
  },
  filterFields: "name,vatId",
};

export const CONFIG = {
  companies: {
    canAdd: true,
    getItems: getCompanies,
    detailsUrl: "/company-details",
    newUrl: "/company",
    tableConfig: COMPANIES_TABLE_CONFIG,
  },
  users: {
    canAdd: false,
    getItems: getUsers,
    detailsUrl: "/user-details",
    newUrl: "/user",
    tableConfig: USERS_TABLE_CONFIG,
  },
  warranties: {
    getItems: getWarranties,
    detailsUrl: "/warranty-details",
    newUrl: "/warranty",
    tableConfig: WARRANTIES_TABLE_CONFIG,
  },
  cars: {
    canAdd: false,
    getItems: getCars,
    detailsUrl: "/car-details",
    newUrl: "/car",
    tableConfig: CARS_TABLE_CONFIG,
  },
};

export const USER_CONFIG = {
  companies: {
    canAdd: false,
    getItems: getCompanies,
    detailsUrl: "/company-details",
    newUrl: "/company",
    tableConfig: COMPANIES_TABLE_CONFIG,
  },
  users: {
    canAdd: false,
    getItems: getUsers,
    detailsUrl: "/user-details",
    newUrl: "/user",
    tableConfig: USERS_TABLE_CONFIG,
  },
  warranties: {
    getItems: getWarranties,
    detailsUrl: "/warranty-details",
    newUrl: "/warranty",
    tableConfig: WARRANTIES_TABLE_CONFIG,
  },
  cars: {
    canAdd: true,
    getItems: getCars,
    detailsUrl: "/car-details",
    newUrl: "/car",
    tableConfig: CARS_TABLE_CONFIG,
  },
};
